import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SingleLanguageSelector } from '../../shared/SingleLanguageSelector';
import { ALL_LANGUAGES, DEFAULT_TOP_LANGUAGE_CODES } from '../../../lib/constants/languages';

interface LanguageSelectionStepProps {
  onSelect: (nativeLanguage: string, targetLanguage: string) => void;
  onBack: () => void;
  targetLanguage: string | null;
}

export function LanguageSelectionStep({
  onSelect,
  onBack,
  targetLanguage: initialTarget,
}: LanguageSelectionStepProps) {
  const { t } = useTranslation('common');
  const [targetLanguage, setTargetLanguage] = useState(initialTarget || '');

  const handleContinue = () => {
    if (targetLanguage) {
      // For the learner path, we use English as default native language
      onSelect('en', targetLanguage);
    }
  };

  const isValid = targetLanguage !== '';

  return (
    <div className="language-selection-step">
      <div className="step-header mb-8 text-center">
        <h3 className="text-2xl font-semibold text-gray-900">
          {t('onboarding.languageSelection.title')}
        </h3>
      </div>

      <div className="language-fields max-w-md mx-auto">
        {/* Target Language */}
        <div className="field-group">
          <SingleLanguageSelector
            value={targetLanguage}
            onChange={setTargetLanguage}
            languages={ALL_LANGUAGES}
            topLanguageCodes={DEFAULT_TOP_LANGUAGE_CODES}
            popoverZIndex="z-[210]"
          />
        </div>
      </div>

      {/* Navigation */}
      <div className="navigation-buttons mt-8 flex justify-between">
        <button
          onClick={onBack}
          className="back-btn flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
        <button
          onClick={handleContinue}
          disabled={!isValid}
          className="continue-btn rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {t('common.next')}
        </button>
      </div>
    </div>
  );
}

